/**
 * The colour of a framework's verdict dot in the navigator, from its R1–R4
 * derivation.
 *
 * Painted with a design variable rather than a Tailwind class, exactly as the
 * prototype does (`style="background:var(--…)"` on its `.fwdot`): the palette
 * carries tones Tailwind's does not, and the dot has to agree with the mask's
 * verdict tint down to the tone.
 *
 * R1 (Rückfrage nötig or offen) is NEUTRAL, not red: the framework is waiting
 * on the supplier, not failing — the concept draws that distinction itself
 * (R2's split pill is red, R1's pill is not). R2 and R3 carry their own fixed
 * colour. R4 defers to `verdictVisual`, the SAME per-verdict decision the unit
 * card's select and the head pill make; routing it through `verdictMeta`'s
 * three severities instead is what once made a `pending` Verordnung amber here
 * and neutral one pane over.
 */
import type { FrameworkDerivation } from "./mapping/deriveFrameworkVerdict"
import { verdictVisual } from "./mapping/verdictDisplay"

/** A variable from the ported design palette (`studioV2.css`). */
export type DotVar = `--${string}`

export function derivationDotVar(
  derivation: FrameworkDerivation | null,
): DotVar {
  if (!derivation) return "--neutral"
  switch (derivation.rule) {
    case "R1":
      return "--neutral"
    case "R2":
      return "--red"
    case "R3":
      return "--doubt"
    case "R4":
      return verdictVisual(derivation.verdict).dotVar as DotVar
  }
}

/** The dot's inline background — `var(--x)`, resolved against `.sv2-root`. */
export const dotStyle = (v: DotVar): { background: string } => ({
  background: `var(${v})`,
})
